import { useEffect, lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { isLoggedInSelector } from '@/store';
import CmsPage from '@spa/pages/CmsPage';
import { getCmsPageRoute, SpaRoutes } from '@spa/constants/routes';
import NotFoundPage from '@spa/pages/NotFoundPage';
import AppLayout from './layouts/AppLayout';
import { getAppSettings } from '@spa/constants/appSettings';
import { B2BSessionSetup } from '@spa/components/B2BSessionSetup';
import PageLoader from './components/PageLoader';
import b2bVerifyBcLoginStatus from '@/utils/b2bVerifyBcLoginStatus';
import { isB2bTokenPage, logoutSession } from '@/utils/b3logout';
import { clearImpersonationTokenCache } from '@spa/utils/impersonationJWTCache';
import { clearCustomerJwtCache } from '@spa/utils/customerJWTCache';

const Providers = lazy(() =>
  import('./providers/Providers').then((m) => ({ default: m.Providers }))
);
const LoginPage = lazy(() => import('@spa/pages/LoginPage'));
const CompanyOrders = lazy(() => import('@spa/pages/CompanyOrders'));
const OrderDetail = lazy(() => import('@spa/pages/OrderDetailsPage'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const AccountSetting = lazy(() => import('@/pages/AccountSetting'));
const ProductsPage = lazy(() => import('@spa/pages/ProductsPage'));
const ReportsPage = lazy(() => import('@spa/pages/ReportsPage'));
const Forecasting = lazy(() => import('@spa/pages/ForecastingPage'));
const ForecastingDetail = lazy(
  () => import('@spa/pages/ForecastingDetailPage')
);
const Checkout = lazy(() => import('@extensions/pages/Checkout'));
const Confirmation = lazy(() => import('@spa/pages/ConfirmationPage'));
const HeadlessController = lazy(
  () => import('@/components/HeadlessController')
);

function AuthRedirect() {
  // @todo: Using appselector is technical debt and should be replaced when we replace the login engine
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const navigate = useNavigate();

  // Extracted from Legacy BC session validation
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkSession = async () => {
      try {
        const isBcLogin = await b2bVerifyBcLoginStatus();

        if (!isBcLogin && isB2bTokenPage()) {
          clearCustomerJwtCache();
          clearImpersonationTokenCache();
          logoutSession();
          navigate(SpaRoutes.LOGOUT, { replace: true });
        }
      } catch (err) {
        console.error('[AuthRedirect] session check error:', err);
      }
    };

    checkSession();
  }, [isLoggedIn, navigate]);

  if (isLoggedIn) {
    // Always redirect to Welcome page if it exists
    const welcomePageSlug = getAppSettings().pages?.welcomePageSlug;
    if (welcomePageSlug) {
      return <Navigate to={getCmsPageRoute(welcomePageSlug)} replace />;
    }
  }

  return <Navigate to={SpaRoutes.LOGOUT} replace />;
}

function AppSetup({ children }: { children: ReactNode }) {
  B2BSessionSetup();
  return <>{children}</>;
}

function App() {
  return (
    <Suspense
      fallback={
        <div className="h-screen">
          <PageLoader />
        </div>
      }
    >
      <Providers>
        <AppSetup>
          <HeadlessController setOpenPage={() => null} />

          <Routes>
            <Route path={SpaRoutes.LOGIN} element={<LoginPage />} />

            <Route element={<AppLayout />}>
              <Route path={SpaRoutes.ROOT} element={<AuthRedirect />} />
              <Route path={SpaRoutes.NOT_FOUND} element={<NotFoundPage />} />
              <Route path={SpaRoutes.CMS_PAGE} element={<CmsPage />} />
              <Route path={SpaRoutes.PRODUCTS} element={<ProductsPage />} />
              <Route path={SpaRoutes.FORECASTING} element={<Forecasting />} />
              <Route
                path={SpaRoutes.FORECASTING_DETAIL}
                element={<ForecastingDetail />}
              />
              <Route
                path={SpaRoutes.COMPANY_ORDERS}
                element={<CompanyOrders />}
              />
              <Route path={SpaRoutes.ORDER_DETAIL} element={<OrderDetail />} />
              <Route path={SpaRoutes.REPORTS} element={<ReportsPage />} />
              <Route
                path={SpaRoutes.USER_MANAGEMENT}
                element={<UserManagement />}
              />
              <Route
                path={SpaRoutes.ACCOUNT_SETTINGS}
                element={<AccountSetting />}
              />
              <Route path={SpaRoutes.CHECKOUT} element={<Checkout />} />
              <Route path={SpaRoutes.CONFIRMATION} element={<Confirmation />} />
            </Route>

            <Route
              path="*"
              element={<Navigate to={SpaRoutes.NOT_FOUND} replace />}
            />
          </Routes>
        </AppSetup>
      </Providers>
    </Suspense>
  );
}

export default App;
